import { FEED_ITEM_ASSIGNMENT_NOTES_MAX } from '@/shared/types/api';

/**
 * SC-423 field rules for the Assign to User modal - the client copy of
 * FeedItemAssignmentRules.Validate on the server. Same rules, same order, same messages, so a
 * form the modal accepts is one the API accepts (and the API stays the authority if they drift).
 *
 * | users | review by | notes | result                         |
 * |-------|-----------|-------|--------------------------------|
 * | >= 1  | empty     | any   | Review by Date required        |
 * | none  | set       | any   | User(s) required               |
 * | none  | empty     | set   | both required                  |
 * | none  | empty     | empty | valid: de-links the item       |
 * | >= 1  | set       | any   | valid                          |
 */
export const ASSIGNMENT_MESSAGES = {
  reviewByDateRequired: 'Review by Date is required when a user is assigned.',
  usersRequired: 'Select at least one user when a Review by Date is set.',
  usersAndDateRequiredForNotes: 'Select at least one user and a Review by Date to save notes.',
  notesTooLong: `Notes cannot exceed ${FEED_ITEM_ASSIGNMENT_NOTES_MAX} characters.`,
} as const;

export interface AssignmentFormValues {
  userIds: string[];
  /** yyyy-MM-dd from <input type="date">, or '' when empty. */
  reviewByDate: string;
  notes: string;
}

/** Which field an error belongs to, so the modal can mark it. */
export type AssignmentField = 'users' | 'reviewByDate' | 'notes';

export interface AssignmentValidationError {
  field: AssignmentField;
  message: string;
}

const hasText = (value: string) => value.trim().length > 0;

/** The first rule the form breaks, or null when it may be saved. */
export function validateAssignment(values: AssignmentFormValues): AssignmentValidationError | null {
  const hasUsers = values.userIds.filter((id) => id.trim()).length > 0;
  const hasDate = hasText(values.reviewByDate);
  const hasNotes = hasText(values.notes);

  if (hasNotes && values.notes.trim().length > FEED_ITEM_ASSIGNMENT_NOTES_MAX) {
    return { field: 'notes', message: ASSIGNMENT_MESSAGES.notesTooLong };
  }
  if (hasUsers && !hasDate) {
    return { field: 'reviewByDate', message: ASSIGNMENT_MESSAGES.reviewByDateRequired };
  }
  if (!hasUsers && hasDate) {
    return { field: 'users', message: ASSIGNMENT_MESSAGES.usersRequired };
  }
  if (!hasUsers && hasNotes) {
    return { field: 'users', message: ASSIGNMENT_MESSAGES.usersAndDateRequiredForNotes };
  }
  return null;
}

/** True when everything is empty - saving then removes the item's assignment. */
export function isClearAssignment(values: AssignmentFormValues): boolean {
  return (
    values.userIds.filter((id) => id.trim()).length === 0 &&
    !hasText(values.reviewByDate) &&
    !hasText(values.notes)
  );
}
